import React, { useState, useMemo } from "react"
import { useSelector } from "react-redux"
import { useAuth0 } from "@auth0/auth0-react"
import {
	convertMinutesToMilitaryTime,
	getDateFormatted,
	getFirstOpenDateTomorrow,
	getFirstOpenDateToday,
	getCurrentTime,
	getClinicTimes,
	getScheduleLockTime,
	humanFormattedDate
} from "../../utils/generalUtils"
import { updateNurseScheduleInMongo } from "../../utils/mongoUtils"
import _ from "lodash"
import useLogging from "../../hooks/useLogging"
import useFetchSettings from "../../hooks/useFetchSettings"
import { PageLoader } from "../Authentication/page-loader"
import { FaLock } from "react-icons/fa"
import StaffingEntryTable from "./StaffingEntryTable"

export const handleSubmit = async (
	tableNurses,
	selectedNurseName,
	selectedRole,
	selectedShiftStart,
	selectedShiftEnd,
	setNotification,
	setEditsMade,
	nextDate,
	getAccessTokenSilently,
	centerOpen,
	testEnvironment,
	centerClosedDates,
	logging
) => {
	// Check if there is an unsubmitted nurse
	if (selectedNurseName || selectedRole || selectedShiftStart || selectedShiftEnd) {
		setNotification({
			visible: true,
			message: "Make sure you click add for the last nurse before clicking update.",
			success: false
		})
		setTimeout(() => {
			setNotification({ visible: false, message: "" })
		}, 3000)
		logging.warn("Make sure you click add for the last nurse before clicking update.")
	}

	// Check for missing required fields and duplicates
	const errors = []
	tableNurses.forEach(nurse => {
		// Check basic requirements
		if (!nurse.nurseName) {
			errors.push(`Nurse is missing a name`)
		}
		if (!nurse.nurseRole) {
			errors.push(`Nurse "${nurse.nurseName}" is missing a role`)
		}
		if (!nurse.scheduledStartTime) {
			errors.push(`Nurse "${nurse.nurseName}" is missing a start time`)
		}
		if (!nurse.scheduledEndTime) {
			errors.push(`Nurse "${nurse.nurseName}" is missing an end time`)
		}
		if (!nurse.nurseId) {
			errors.push(`Nurse "${nurse.nurseName}" is missing a nurse ID`)
		}

		// Check for duplicates
		const duplicateCount = tableNurses.filter(
			otherNurse =>
				otherNurse.nurseName === nurse.nurseName && otherNurse.nurseRole === nurse.nurseRole
		).length

		if (duplicateCount > 1) {
			errors.push(
				`Nurse "${nurse.nurseName}" is listed multiple times with role "${nurse.nurseRole}"`
			)
		}
	})

	// Remove duplicate error messages
	const uniqueErrors = [...new Set(errors)]

	if (uniqueErrors.length > 0) {
		setNotification({
			visible: true,
			message: uniqueErrors.join("\n"),
			success: false
		})
		logging.warn("Schedule validation errors:", uniqueErrors)
		setTimeout(() => {
			setNotification({ visible: false, message: "" })
		}, 5000)
		return false
	}

	// Check for default nurses (notification will be removed, just logging)
	const defaultNurses = tableNurses.filter(nurse =>
		nurse.nurseName?.toUpperCase().includes("DEFAULT")
	)
	if (defaultNurses.length > 0) {
		logging.warn("Tomorrow's schedule contains default nurses")
	}

	// TODO: this is problematic... kinda...

	if (
		_.isEqual(
			getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates),
			getFirstOpenDateTomorrow(centerOpen, testEnvironment, centerClosedDates)
		)
	) {
		setNotification({
			visible: true,
			message:
				"Unable to submit tomorrow's schedule with patients already allocated to nurses.",
			success: false
		})
		logging.warn(
			"Unable to submit tomorrow's schedule with patients already allocated to nurses."
		)
		setTimeout(() => {
			setNotification({ visible: false, message: "" })
		}, 3000)
		return false
	}

	// Check if nurse staffing is within valid range
	if (tableNurses.length <= 4) {
		setNotification({
			visible: true,
			message:
				"Nurse staffing for tomorrow should include more than 4 nurses. Please update the nurse schedule.",
			success: false
		})
		setTimeout(() => {
			setNotification({ visible: false, message: "" })
		}, 3000)
		logging.warn(
			"Nurse staffing for tomorrow should include more than 4 nurses. Please update the nurse schedule."
		)
		return false
	}

	const chargeNurseCount = tableNurses.filter(nurse => nurse.nurseRole === "Charge").length
	if (chargeNurseCount !== 1) {
		setNotification({
			visible: true,
			message:
				"There must be exactly one charge nurse scheduled for tomorrow. Please update the nurse schedule.",
			success: false
		})
		setTimeout(() => {
			setNotification({ visible: false, message: "" })
		}, 3000)
		logging.warn(
			"There must be exactly one charge nurse scheduled for tomorrow. Please update the nurse schedule."
		)
		return false
	}

	// StaffingEntryTable already returns nurses in the correct format (minutes)
	// Filter out UI-only fields before saving to MongoDB
	const updatedTomorrowNurses = tableNurses.map(({ defaultShiftLength, _id, ...rest }) => rest)

	const chargeNurse = updatedTomorrowNurses.find(nurse => nurse.nurseRole === "Charge")
	const regularNurses = updatedTomorrowNurses.filter(nurse => nurse.nurseRole !== "Charge")

	try {
		const token = await getAccessTokenSilently({
			audience: process.env.REACT_APP_API_IDENTIFIER
		})

		await updateNurseScheduleInMongo(
			token,
			nextDate,
			setNotification,
			false,
			false,
			regularNurses,
			chargeNurse,
			logging
		)

		setEditsMade(false)
		setNotification({
			visible: true,
			message: "Tomorrow's nurse schedule has been updated successfully",
			success: true
		})
		setTimeout(() => {
			setNotification({ visible: false, message: "" })
		}, 3000)
		logging.info("Tomorrow's nurse schedule has been updated successfully")
		return true
	} catch (error) {
		logging.error("Error saving schedule:", { error: error.message, stack: error.stack })
		return false
	}
}

// Export the prepareDataForTable function (simplified - StaffingEntryTable now handles conversions)
export const prepareDataForTable = nurseData => {
	// StaffingEntryTable now handles all time format conversions internally
	return nurseData
}

const NurseTable = ({
	setNotification,
	editsMade,
	setEditsMade,
	tableNurses,
	setTableNurses,
	selectedNurseName,
	selectedRole,
	selectedShiftStart,
	selectedShiftEnd,
	selectedShift,
	setSelectedNurseName,
	setSelectedRole,
	setSelectedShiftStart,
	setSelectedShiftEnd,
	setSelectedShift
}) => {
	const { user } = useAuth0()
	const { getAccessTokenSilently } = useAuth0()
	const logging = useLogging()

	const sandbox = useSelector(state => state.sandbox.isSandboxMode)

	// Memoized selectors
	const { settingsData } = useFetchSettings()
	const settingsNurses = settingsData?.nurses
	const centerOpen = settingsData?.centerOpen
	const centerClosedDates = settingsData?.center_closed_dates || []

	const testEnvironment = useMemo(() => user.org_id === "org_lkg3WMhi3Q2l92iT", [user.org_id])

	const nextDate = useMemo(
		() =>
			getDateFormatted(
				getFirstOpenDateTomorrow(centerOpen, testEnvironment, centerClosedDates)
			),
		// centerClosedDates is stable in practice (read from settings); wrapping each call site adds noise.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[centerOpen, testEnvironment, centerClosedDates]
	)

	// Memoize the computed times using date-aware methods
	const { nextDateOpenTime, nextDateCloseTime } = useMemo(() => {
		const tomorrowDate = getFirstOpenDateTomorrow(
			centerOpen,
			testEnvironment,
			centerClosedDates
		)
		const { openTime: openTimeMinutes, closeTime: closeTimeMinutes } = getClinicTimes(
			settingsData,
			tomorrowDate
		)

		return {
			nextDateOpenTime: convertMinutesToMilitaryTime(openTimeMinutes),
			nextDateCloseTime: convertMinutesToMilitaryTime(closeTimeMinutes)
		}
		// centerClosedDates is stable in practice (read from settings); wrapping each call site adds noise.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [centerOpen, testEnvironment, centerClosedDates, settingsData])

	const [, setIsUpdateHovered] = useState(false)
	const currentTime = getCurrentTime(sandbox, testEnvironment)

	// Calculate lock time based on whether today ends early
	const lockTime = useMemo(() => {
		const today = getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)
		return getScheduleLockTime(
			today,
			settingsData?.close_time_minutes || [],
			settingsData?.holiday_close_time_minutes || [],
			settingsData?.holidays || []
		)
		// centerClosedDates is stable in practice (read from settings); wrapping each call site adds noise.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [centerOpen, testEnvironment, centerClosedDates, settingsData])

	const isEnabled = currentTime < lockTime

	// Callback functions for the shared StaffingEntryTable component
	const handleAddNurse = nurseToAdd => {
		const updatedNurses = [...tableNurses, nurseToAdd]
		setTableNurses(updatedNurses)
		setEditsMade(true)
	}

	const handleUpdateNurse = (index, updatedNurse) => {
		const updatedNurses = tableNurses.map((nurse, i) => (i === index ? updatedNurse : nurse))
		setTableNurses(updatedNurses)
		setEditsMade(true)
	}

	const handleDeleteNurse = index => {
		const updatedNurses = tableNurses.filter((_, i) => i !== index)
		setTableNurses(updatedNurses)
		setEditsMade(true)
	}

	const isLocked = useSelector(state => state.schedule.isLocked)

	if (!tableNurses || !settingsNurses) {
		return <PageLoader />
	}

	const title = (
		<span className="flex items-center justify-center">
			{!isEnabled && (
				<span
					className="mr-2 text-gray-500 flex items-center"
					title="Nurse schedule can only be modified before 11 AM"
				>
					<FaLock className="h-5 w-5" />
				</span>
			)}
			Nurse Schedule -{" "}
			{humanFormattedDate(
				getFirstOpenDateTomorrow(centerOpen, testEnvironment, centerClosedDates)
			)}
		</span>
	)

	return (
		<div className="flex flex-col min-h-screen">
			<div className="flex-1">
				<StaffingEntryTable
					nurses={tableNurses}
					onAddNurse={handleAddNurse}
					onUpdateNurse={handleUpdateNurse}
					onDeleteNurse={handleDeleteNurse}
					settingsNurses={settingsNurses}
					openTime={nextDateOpenTime}
					closeTime={nextDateCloseTime}
					readOnly={!isEnabled}
					sandbox={sandbox}
					showAddRow={isEnabled}
					title={title}
					containerClassName="mt-10 flex flex-col"
				/>
			</div>

			{isLocked && (
				<div className="flex justify-center py-6 border-t border-gray-200 bg-white sticky bottom-0">
					<div className="relative">
						<button
							onClick={() =>
								handleSubmit(
									tableNurses,
									selectedNurseName,
									selectedRole,
									selectedShiftStart,
									selectedShiftEnd,
									setNotification,
									setEditsMade,
									nextDate,
									getAccessTokenSilently,
									centerOpen,
									testEnvironment,
									centerClosedDates,
									logging
								)
							}
							onMouseEnter={() => setIsUpdateHovered(true)}
							onMouseLeave={() => setIsUpdateHovered(false)}
							className={`py-3 px-6 rounded-lg font-medium transition-colors duration-200 ${
								editsMade && isEnabled
									? "bg-customBlue text-white hover:bg-customBlue-dark shadow-lg"
									: "bg-gray-300 text-gray-500 cursor-not-allowed"
							}`}
							disabled={!editsMade || !isEnabled}
						>
							Update Staffing Information
						</button>
					</div>
				</div>
			)}
		</div>
	)
}

export default NurseTable
